import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ArrowLeft, 
  Clock, 
  Car,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${window.location.origin}/api`;

function ReminderActionPage() {
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [action, setAction] = useState(""); // snooze or not-my-vehicle
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const processAction = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const actionParam = params.get("action");

    if (!token || !actionParam) {
      return { error: true, message: "Invalid link. The link may have expired or is incomplete." };
    }

    // Determine which API endpoint to call
    let endpoint = "";
    if (actionParam === "snooze") {
      endpoint = `${API}/reminders/snooze`;
    } else if (actionParam === "not-my-vehicle") {
      endpoint = `${API}/reminders/not-my-vehicle`;
    } else {
      return { error: true, message: "Unknown action requested." };
    }

    try {
      const response = await axios.get(endpoint, { params: { token } });
      return { error: false, data: response.data, action: actionParam };
    } catch (err) {
      return { 
        error: true, 
        message: err.response?.data?.detail || "Failed to process your request. The link may be invalid or expired."
      };
    }
  }, []);

  useEffect(() => {
    processAction().then(result => {
      if (result.error) {
        setStatus("error");
        setErrorMessage(result.message);
      } else {
        setStatus("success");
        setData(result.data);
        setAction(result.action);
      }
    });
  }, [processAction]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="py-8 text-center">
          {/* Loading State */}
          {status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Processing your request...</p>
            </>
          )}
          
          {/* Success State - Snooze */}
          {status === "success" && action === "snooze" && (
            <>
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Reminder Snoozed</h1>
              <p className="text-gray-600 mb-4">
                We&apos;ve snoozed reminders for{" "}
                <strong className="text-gray-900">{data?.registration}</strong>
              </p>
              {data?.snooze_until && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 font-medium">
                    We&apos;ll remind you again on
                  </p>
                  <p className="text-blue-900 text-xl font-bold mt-1">
                    {data.snooze_until}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-500 mb-6">
                You can always manage your reminders from your dashboard.
              </p>
              <a href="/dashboard">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              </a>
            </>
          )}
          
          {/* Success State - Not My Vehicle */}
          {status === "success" && action === "not-my-vehicle" && (
            <>
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Vehicle Removed</h1>
              <p className="text-gray-600 mb-2">
                We&apos;ve removed{" "}
                <strong className="text-gray-900">{data?.registration}</strong>
                {data?.vehicle_info && (
                  <span className="block text-sm text-gray-500 mt-1">
                    ({data.vehicle_info})
                  </span>
                )}
              </p>
              <p className="text-gray-600 mb-6">
                from your reminders.
              </p>
              
              {/* Prompt to add new vehicle */}
              {data?.prompt_new_vehicle && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 text-amber-800 font-medium mb-2">
                    <Car className="h-5 w-5" />
                    <span>Would you like to check and be reminded about another vehicle?</span>
                  </div>
                  <a href="/dashboard">
                    <Button className="bg-amber-600 hover:bg-amber-700 mt-2">
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Vehicle
                    </Button>
                  </a>
                </div>
              )}
              
              <div className="flex flex-col gap-2">
                <a href="/reminders">
                  <Button variant="outline" className="w-full">
                    Manage All Reminders
                  </Button>
                </a>
                <a href="/">
                  <Button variant="ghost" className="w-full text-gray-500">
                    Go to Homepage
                  </Button>
                </a>
              </div>
            </>
          )}
          
          {/* Error State */}
          {status === "error" && (
            <>
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h1>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <div className="space-y-2">
                <a href="/dashboard">
                  <Button className="bg-blue-600 hover:bg-blue-700 w-full">
                    Go to Dashboard
                  </Button>
                </a>
                <a href="/reminders">
                  <Button variant="outline" className="w-full">
                    Manage Reminders
                  </Button>
                </a>
                <a href="/">
                  <Button variant="ghost" className="w-full text-gray-500">
                    Go to Homepage
                  </Button>
                </a>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ReminderActionPage;
